import React, { useCallback, useEffect, useRef } from 'react';
import { Panel } from '../panels/Panel';
import { PanelSplitter } from '../panels/PanelSplitter';
import { usePanelStore } from '../../store/panelStore';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import { useSlotAssetGenerator } from '../../hooks/useSlotAssetGenerator';
import { SlotGenerationOptions } from './SlotGenerationOptions';
import { ComponentLibrary } from './ComponentLibrary';
import { SlotPromptPanel } from './SlotPromptPanel';
import { SlotGeneratingView } from './SlotGeneratingView';
import { SlotErrorBanner } from './SlotErrorBanner';
import { SlotResultsModal } from './SlotResultsModal';
import { SlotLightbox } from './SlotLightbox';
import { SlotToastBar } from './SlotToastBar';
import { SlotHistoryPanel } from './SlotHistoryPanel';
import { ApiKeySettings } from './ApiKeySettings';

export const SlotAssetGeneratorPanel: React.FC = () => {
  const panel = usePanelStore((state) => state.panels['slot-asset-generator']);
  const generation = useSlotAssetGeneratorStore((s) => s.generation);
  const resultsModalOpen = useSlotAssetGeneratorStore((s) => s.resultsModalOpen);
  const lightboxIndex = useSlotAssetGeneratorStore((s) => s.lightboxIndex);
  const leftPanelWidth = useSlotAssetGeneratorStore((s) => s.leftPanelWidth);
  const historyPanelOpen = useSlotAssetGeneratorStore((s) => s.historyPanelOpen);
  const panelRef = useRef<HTMLDivElement>(null);

  const { generate, cancel, reroll, saveImage } = useSlotAssetGenerator();

  const handleRetry = useCallback(() => {
    if (generation.request) {
      generate(generation.request);
    }
  }, [generate, generation.request]);

  const handleRegenerateAll = useCallback(() => {
    if (generation.request) {
      generate(generation.request);
    }
  }, [generate, generation.request]);

  // Resize handler -- uses getState() to avoid stale closure during rapid drag
  const handleLeftResize = useCallback((delta: number) => {
    const current = useSlotAssetGeneratorStore.getState().leftPanelWidth;
    useSlotAssetGeneratorStore.getState().setLeftPanelWidth(current + delta);
  }, []);

  // Global keyboard shortcuts for this panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!panel?.visible) return;

      // Escape: close lightbox first, then results modal
      if (e.key === 'Escape') {
        if (lightboxIndex !== null) {
          useSlotAssetGeneratorStore.getState().closeLightbox();
          e.stopPropagation();
        } else if (resultsModalOpen) {
          useSlotAssetGeneratorStore.getState().closeResultsModal();
          e.stopPropagation();
        }
        return;
      }

      // Left/Right: lightbox navigation
      if (lightboxIndex !== null) {
        if (e.key === 'ArrowLeft') {
          const total = generation.results.length;
          const prev = lightboxIndex > 0 ? lightboxIndex - 1 : total - 1;
          useSlotAssetGeneratorStore.getState().openLightbox(prev);
          e.preventDefault();
        } else if (e.key === 'ArrowRight') {
          const total = generation.results.length;
          const next = lightboxIndex < total - 1 ? lightboxIndex + 1 : 0;
          useSlotAssetGeneratorStore.getState().openLightbox(next);
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [panel?.visible, lightboxIndex, resultsModalOpen, generation.results.length]);

  // Focus management: after generation completes, focus results modal
  useEffect(() => {
    if (resultsModalOpen && panelRef.current) {
      const modal = panelRef.current.querySelector('[class*="z-[100]"]');
      if (modal instanceof HTMLElement) {
        modal.focus();
      }
    }
  }, [resultsModalOpen]);

  if (!panel) return null;

  return (
    <Panel panel={panel}>
      <div ref={panelRef} className="flex h-full bg-zinc-900 overflow-hidden relative">
        {/* Left Panel: resizable */}
        <div
          style={{ width: leftPanelWidth }}
          className="flex flex-col overflow-hidden flex-shrink-0"
        >
          <SlotGenerationOptions />
          <ComponentLibrary />
        </div>

        {/* Resize divider */}
        <PanelSplitter direction="horizontal" onResize={handleLeftResize} />

        {/* Center Panel: fills remaining space */}
        <div className="flex-1 min-w-[300px] relative overflow-hidden flex flex-col">
          {generation.status === 'error' && (
            <SlotErrorBanner onRetry={handleRetry} />
          )}
          {generation.status === 'generating' ? (
            <SlotGeneratingView onCancel={cancel} />
          ) : (
            <SlotPromptPanel onGenerate={generate} />
          )}

        </div>

        {/* Right Panel: History (collapsible) */}
        {historyPanelOpen && (
          <>
            <PanelSplitter direction="horizontal" onResize={() => {}} />
            <div className="w-[260px] flex-shrink-0 border-l border-zinc-800 bg-zinc-900 overflow-hidden">
              <SlotHistoryPanel />
            </div>
          </>
        )}

        {/* Results Modal overlay */}
        {resultsModalOpen && (
          <SlotResultsModal
            onSaveImage={saveImage}
            onReroll={reroll}
            onRegenerateAll={handleRegenerateAll}
          />
        )}

        {/* Lightbox overlay */}
        {lightboxIndex !== null && (
          <SlotLightbox
            onSaveImage={saveImage}
            onReroll={reroll}
          />
        )}

        {/* Toast notifications */}
        <SlotToastBar />

        {/* API Key Settings modal (self-contained, only shows when isSettingsOpen) */}
        <ApiKeySettings />
      </div>
    </Panel>
  );
};
